import { Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { useEffect, useState } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const FEATURES = [
  { icon: '⚡', title: 'Instant payments', desc: 'Customers scan a QR code and pay in seconds. No card reader, no account, no waiting.' },
  { icon: '🔒', title: 'Direct to your wallet', desc: 'Money goes straight to your BCH address — no middleman, no processor, no fees.' },
  { icon: '🛍️', title: 'Built-in menu', desc: 'Add your products and let staff tap items to build orders automatically.' },
  { icon: '🧾', title: 'Receipts & history', desc: 'Every sale is saved locally. Share digital receipts and export CSV for your records.' },
  { icon: '🌍', title: 'Works anywhere', desc: 'Supports dozens of currencies. Install on your phone — works offline, no app store needed.' },
  { icon: '🙅', title: 'No account required', desc: "Open the app, enter your BCH address, and start accepting payments. That's it." },
];

const HOW_IT_WORKS = [
  { step: '1', title: 'Enter the amount', desc: 'Type the sale total on the keypad, or tap items from your menu.' },
  { step: '2', title: 'Customer scans the QR', desc: 'Show them the screen. They scan with any BCH wallet and confirm.' },
  { step: '3', title: 'Payment confirmed', desc: 'The app detects the payment automatically and shows a green confirmation.' },
];

const INSTALL_PLATFORMS = [
  {
    id: 'iphone',
    icon: '🍎',
    label: 'iPhone & iPad',
    badge: 'Safari',
    steps: [
      'Open this page in Safari',
      <>Tap the <strong>Share button</strong> <span className="inline-flex items-center bg-zinc-200 rounded px-1 text-[11px] font-mono">□↑</span> at the bottom</>,
      <>Scroll down and tap <strong>"Add to Home Screen"</strong></>,
                  <>Tap <strong>"Add"</strong> — done!</>,
    ],
  },
  {
    id: 'android',
    icon: '🤖',
    label: 'Android',
    badge: 'Chrome',
    steps: [
      'Open this page in Chrome',
      <>Tap the <strong>three-dot menu ⋮</strong> top-right</>,
      <>Tap <strong>"Add to Home screen"</strong> or <strong>"Install app"</strong></>,
      <>Tap <strong>"Install"</strong> — done!</>,
    ],
  },
  {
    id: 'desktop',
    icon: '💻',
    label: 'Desktop',
    badge: 'Chrome / Edge',
    steps: [
      'Open this page in Chrome or Edge',
      <>Look for the <strong>install icon ⊕</strong> in the address bar</>,
      <>Click <strong>"Install BCHPAY.Cash"</strong></>,
      <>Click <strong>"Install"</strong> — done!</>,
    ],
  },
];

// ─── Phone mockup ─────────────────────────────────────────────────────────────

function PhoneMockup() {
  return (
    <div className="relative w-full max-w-[220px]">
      <div className="bg-zinc-900 rounded-[2.2rem] p-2 shadow-2xl shadow-zinc-900/40">
        <div className="bg-zinc-50 rounded-[1.8rem] overflow-hidden">
          <div className="bg-white px-5 pt-3 pb-2 flex items-center justify-between">
            <span className="text-[10px] font-bold text-zinc-400">9:41</span>
            <div className="w-14 h-3.5 bg-zinc-900 rounded-full" />
            <span className="text-[10px] font-bold text-zinc-400">100%</span>
          </div>
          <div className="bg-white border-b border-zinc-100 px-3 py-2 flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-[#0AC18E] flex items-center justify-center shrink-0">
              <span className="text-white font-black text-[8px]">BCH</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold text-zinc-900 truncate">My Store</p>
            </div>
            <div className="bg-[#0AC18E]/10 rounded-full px-1.5 py-0.5">
              <span className="text-[8px] font-bold text-[#0AC18E]">$350</span>
            </div>
          </div>
          <div className="bg-white mx-2.5 mt-2.5 rounded-xl px-3 py-3 text-center shadow-sm border border-zinc-100">
            <p className="text-2xl font-black text-zinc-900 tabular-nums">25<span className="text-zinc-300">.00</span></p>
            <p className="text-[9px] font-mono text-[#0AC18E] mt-0.5">≈ 0.07142 BCH</p>
          </div>
          <div className="grid grid-cols-3 gap-1 px-2.5 mt-2">
            {['1','2','3','4','5','6','7','8','9','.','0','⌫'].map((k) => (
              <div key={k} className="h-7 rounded-lg bg-white border border-zinc-100 flex items-center justify-center">
                <span className="text-[10px] font-semibold text-zinc-700">{k}</span>
              </div>
            ))}
          </div>
          <div className="px-2.5 mt-2 mb-3">
            <div className="h-8 rounded-xl bg-[#0AC18E] flex items-center justify-center">
              <span className="text-white text-[10px] font-black">Charge →</span>
            </div>
          </div>
          <div className="bg-white border-t border-zinc-100 px-2 py-1.5 flex">
            {[['💳','Checkout'],['🛍️','Menu'],['📋','History'],['⚙️','Settings']].map(([icon, label]) => (
              <div key={label} className="flex-1 flex flex-col items-center gap-0.5">
                <span className="text-xs">{icon}</span>
                <span className={`text-[7px] font-medium ${label === 'Checkout' ? 'text-[#0AC18E]' : 'text-zinc-400'}`}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="absolute -inset-6 bg-[#0AC18E]/10 rounded-full blur-3xl -z-10" />
    </div>
  );
}

// ─── Install section ──────────────────────────────────────────────────────────

function InstallSection() {
  const [open, setOpen] = useState<string | null>(null);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installing, setInstalling] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(display-mode: standalone)');
    setInstalled(mq.matches || (navigator as unknown as { standalone?: boolean }).standalone === true);
    const handler = (e: MediaQueryListEvent) => setInstalled(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleNativeInstall = async () => {
    if (!installPrompt) return;
    setInstalling(true);
    await installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') { setInstallPrompt(null); setInstalled(true); }
    setInstalling(false);
  };

  return (
    <section id="install" className="max-w-4xl mx-auto px-5 py-16">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 bg-zinc-900 dark:bg-zinc-800 rounded-full px-4 py-1.5 mb-5">
          <span className="text-base">📲</span>
          <span className="text-xs font-bold text-white uppercase tracking-wider">No App Store Required</span>
        </div>
        <h2 className="text-2xl font-black mb-2">Install BCHPAY.Cash on your device</h2>
        <p className="text-sm text-zinc-500 dark:text-zinc-400 max-w-md mx-auto">
          Works like a native app — full screen, offline capable, home screen icon. No download, no App Store, no Google Play.
        </p>
      </div>

      {/* Device grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        {[
          { icon: '📱', label: 'iPhone' },
          { icon: '🤖', label: 'Android' },
          { icon: '🖥️', label: 'Tablet' },
          { icon: '💻', label: 'Desktop' },
        ].map(({ icon, label }) => (
          <div key={label} className="bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-2xl py-4 flex flex-col items-center gap-2">
            <span className="text-3xl">{icon}</span>
            <span className="text-sm font-semibold text-zinc-700 dark:text-zinc-200">{label}</span>
            <span className="text-[10px] text-[#0AC18E] font-bold uppercase tracking-wide">✓ Supported</span>
          </div>
        ))}
      </div>

      {/* One-tap install (Android/Chrome desktop) */}
      {!installed && installPrompt && (
        <button
          onClick={handleNativeInstall}
          disabled={installing}
          className="w-full mb-6 py-4 rounded-2xl bg-[#0AC18E] text-white text-base font-bold shadow-lg shadow-[#0AC18E]/30 hover:bg-[#09AF81] active:scale-98 transition-all disabled:opacity-70 flex items-center justify-center gap-2"
        >
          <span className="text-xl">📲</span>
          {installing ? 'Installing…' : 'Install App Now — One Tap'}
        </button>
      )}

      {installed && (
        <div className="w-full mb-6 py-4 rounded-2xl bg-[#0AC18E]/10 border border-[#0AC18E]/30 text-[#0AC18E] text-sm font-bold text-center">
          ✓ Already installed on this device
        </div>
      )}

      {/* Platform instructions */}
      <div className="space-y-3">
        {INSTALL_PLATFORMS.map((p) => (
          <div key={p.id} className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-100 dark:border-zinc-800 overflow-hidden">
            <button
              onClick={() => setOpen(open === p.id ? null : p.id)}
              className="w-full flex items-center gap-3 px-5 py-4 text-left"
            >
              <span className="text-2xl shrink-0">{p.icon}</span>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-zinc-800 dark:text-zinc-100 text-sm">{p.label}</p>
                <p className="text-xs text-zinc-400">via {p.badge}</p>
              </div>
              <span className={`text-zinc-400 text-sm transition-transform shrink-0 ${open === p.id ? 'rotate-180' : ''}`}>▾</span>
            </button>
            {open === p.id && (
              <div className="px-5 pb-5 pt-1 border-t border-zinc-100 dark:border-zinc-800 space-y-3">
                {p.steps.map((step, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#0AC18E] text-white text-xs font-black flex items-center justify-center shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    <p className="text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed">{step}</p>
                  </div>
                ))}
                <div className="bg-[#0AC18E]/10 rounded-xl px-4 py-3 border border-[#0AC18E]/20 mt-2">
                  <p className="text-xs text-zinc-600 dark:text-zinc-300">
                    <span className="font-bold text-[#0AC18E]">✓ Done!</span> BCHPAY.Cash opens full-screen from your home screen like a native app.
                  </p>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <p className="text-center text-xs text-zinc-400 mt-5">
        Your settings and transaction history are saved on your device. Nothing is uploaded to a server.
      </p>
    </section>
  );
}

// ─── In-app install banner (shown inside /app if not installed) ───────────────

export function InstallBanner() {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('bch-pos:install-dismissed') === '1');
  const [installed, setInstalled] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(display-mode: standalone)');
    setInstalled(mq.matches || (navigator as unknown as { standalone?: boolean }).standalone === true);
    const handler = (e: MediaQueryListEvent) => setInstalled(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleDismiss = () => {
    localStorage.setItem('bch-pos:install-dismissed', '1');
    setDismissed(true);
  };

  const handleInstall = async () => {
    if (!installPrompt) return;
    setInstalling(true);
    await installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') { setInstallPrompt(null); setInstalled(true); }
    setInstalling(false);
  };

  // Hide if: already installed, dismissed, or no prompt available (and not iOS)
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (installed || dismissed || (!installPrompt && !isIOS)) return null;

  return (
    <div className="mx-4 mt-3 bg-zinc-900 dark:bg-zinc-800 rounded-2xl px-4 py-3 flex items-center gap-3 border border-zinc-700">
      <div className="w-9 h-9 rounded-xl bg-[#0AC18E] flex items-center justify-center shrink-0">
        <span className="text-white font-black text-xs">BCH</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold text-white leading-snug">Install BCHPAY.Cash</p>
        <p className="text-[11px] text-zinc-400 leading-snug">Fullscreen mode &amp; faster checkout</p>
      </div>
      {installPrompt ? (
        <button
          onClick={handleInstall}
          disabled={installing}
          className="shrink-0 px-3 py-1.5 rounded-xl bg-[#0AC18E] text-white text-xs font-bold hover:bg-[#09AF81] transition-colors disabled:opacity-60"
        >
          {installing ? '…' : 'Install'}
        </button>
      ) : (
        <Link
          to="/"
          className="shrink-0 px-3 py-1.5 rounded-xl bg-[#0AC18E] text-white text-xs font-bold hover:bg-[#09AF81] transition-colors"
          onClick={() => document.getElementById('install')?.scrollIntoView({ behavior: 'smooth' })}
        >
          How?
        </Link>
      )}
      <button onClick={handleDismiss} className="text-zinc-500 hover:text-zinc-300 text-lg leading-none shrink-0 ml-1" aria-label="Dismiss">×</button>
    </div>
  );
}

// ─── Main splash page ─────────────────────────────────────────────────────────

export default function SplashPage() {
  useSeoMeta({
    title: 'BCHPAY.Cash — Accept Bitcoin Cash & Get Discovered',
    description: 'Free Bitcoin Cash POS terminal + BCH Local merchant discovery. No account, no fees, no middleman. Get found by BCH users near you.',
  });

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-zinc-50">

      {/* ── Nav ── */}
      <header className="sticky top-0 z-10 bg-white/80 dark:bg-zinc-900/80 backdrop-blur border-b border-zinc-100 dark:border-zinc-800">
        <div className="max-w-4xl mx-auto px-5 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[#0AC18E] flex items-center justify-center shrink-0">
              <span className="text-white font-black text-xs">BCH</span>
            </div>
            <span className="font-bold text-sm text-zinc-900 dark:text-zinc-50">BCHPAY.Cash</span>
          </div>
          <div className="flex items-center gap-2">
            <a href="https://bchlocal.com" target="_blank" rel="noopener noreferrer" className="hidden sm:block px-3 py-2 rounded-xl text-sm font-semibold text-zinc-500 dark:text-zinc-400 hover:text-[#0AC18E] transition-colors">
              📍 BCH Local
            </a>
            <a href="#install" className="hidden sm:block px-3 py-2 rounded-xl text-sm font-semibold text-zinc-500 dark:text-zinc-400 hover:text-[#0AC18E] transition-colors">
              📲 Install
            </a>
            <Link
              to="/app"
              className="px-4 py-2 rounded-xl bg-[#0AC18E] text-white text-sm font-bold shadow-sm shadow-[#0AC18E]/30 hover:bg-[#09AF81] transition-colors"
            >
              Open App →
            </Link>
          </div>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="max-w-4xl mx-auto px-5 pt-16 pb-10 text-center">
        <div className="inline-flex items-center gap-2 bg-[#0AC18E]/10 dark:bg-[#0AC18E]/20 border border-[#0AC18E]/20 rounded-full px-4 py-1.5 mb-6">
          <span className="w-2 h-2 rounded-full bg-[#0AC18E] animate-pulse" />
          <span className="text-xs font-semibold text-[#0AC18E] uppercase tracking-wider">Free &amp; Open Source</span>
        </div>

        <h1 className="text-4xl sm:text-5xl font-black leading-tight mb-4">
          Accept{' '}
          <span className="text-[#0AC18E]">Bitcoin Cash</span>
          <br />
          at your business
        </h1>

        <p className="text-lg text-zinc-500 dark:text-zinc-400 max-w-xl mx-auto mb-8 leading-relaxed">
          A simple point-of-sale terminal that runs in any browser.
          No account, no monthly fees, no card reader — just instant payments straight to your wallet.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/app"
            className="w-full sm:w-auto px-8 py-4 rounded-2xl bg-[#0AC18E] text-white text-base font-bold shadow-lg shadow-[#0AC18E]/30 hover:bg-[#09AF81] active:scale-95 transition-all"
          >
            Launch POS Terminal →
          </Link>
          <a
            href="#install"
            className="w-full sm:w-auto px-8 py-4 rounded-2xl bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 text-base font-semibold border border-zinc-200 dark:border-zinc-700 hover:border-[#0AC18E]/40 transition-colors flex items-center justify-center gap-2"
          >
            <span>📲</span> Install on device
          </a>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-4 mt-10 text-xs text-zinc-400 font-medium">
          {['No sign-up', 'No monthly fees', 'No middleman', 'Works offline', 'Open source'].map((b) => (
            <span key={b} className="flex items-center gap-1.5">
              <span className="text-[#0AC18E]">✓</span> {b}
            </span>
          ))}
        </div>
      </section>

      {/* ── Phone mockup ── */}
      <section className="max-w-4xl mx-auto px-5 pb-14 flex justify-center">
        <PhoneMockup />
      </section>

      {/* ── BCH Local discovery section ── */}
      <section className="bg-zinc-900 dark:bg-zinc-950 border-y border-zinc-800">
        <div className="max-w-4xl mx-auto px-5 py-16">

          {/* Badge */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 bg-[#0AC18E]/15 border border-[#0AC18E]/30 rounded-full px-4 py-1.5">
              <span className="w-2 h-2 rounded-full bg-[#0AC18E] animate-pulse" />
              <span className="text-xs font-bold text-[#0AC18E] uppercase tracking-wider">BCH Local</span>
            </div>
          </div>

          {/* Headline */}
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-4 leading-tight">
              Get discovered by<br />
              <span className="text-[#0AC18E]">BCH users near you</span>
            </h2>
            <p className="text-zinc-400 text-base max-w-xl mx-auto leading-relaxed">
              BCHPAY.Cash is more than a payment terminal. Enable BCH Local discovery and
              your business appears in the BCH merchant directory — helping BCH users
              find places to spend Bitcoin Cash.
            </p>
          </div>

          {/* Two-column layout: features + flow */}
          <div className="grid sm:grid-cols-2 gap-8 items-center mb-12">

            {/* Feature list */}
            <div className="space-y-4">
              {[
                ['📍', 'Merchant directory listing', 'Your business name, category, and location appear on BCH Local for anyone to find.'],
                ['✅', 'Verified merchant badge', 'A green "Active BCH Merchant" badge shows customers you\'re genuinely accepting BCH.'],
                ['📡', 'BCH activity status', 'Real-time signal shows when you\'ve recently transacted — no stale listings.'],
                ['🖼️', 'Storefront photo support', 'Upload your logo so customers recognise you in the directory.'],
                ['⚡', 'Enable with one click', 'Flip a switch in Settings. No forms, no approval, no waiting.'],
              ].map(([icon, title, desc]) => (
                <div key={title as string} className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-zinc-800 border border-zinc-700 flex items-center justify-center shrink-0 text-lg">
                    {icon}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-white mb-0.5">{title as string}</p>
                    <p className="text-xs text-zinc-400 leading-relaxed">{desc as string}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Visual flow */}
            <div className="flex flex-col items-center">
              <div className="w-full max-w-xs space-y-2">
                {[
                  { icon: '💳', label: 'Accept BCH', sub: 'Instant payments, no fees', active: true },
                  { icon: '📡', label: 'Enable BCH Local', sub: 'One switch in Settings', active: true },
                  { icon: '📍', label: 'Appear on BCH Local', sub: 'Listed in the directory', active: true },
                  { icon: '🚀', label: 'Get Discovered', sub: 'BCH users find your business', active: true },
                ].map(({ icon, label, sub, active }, i, arr) => (
                  <div key={label}>
                    <div className={`flex items-center gap-3 rounded-2xl px-4 py-3 border ${
                      i === arr.length - 1
                        ? 'bg-[#0AC18E] border-[#0AC18E]'
                        : 'bg-zinc-800 border-zinc-700'
                    }`}>
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 text-lg ${
                        i === arr.length - 1 ? 'bg-white/20' : 'bg-zinc-700'
                      }`}>
                        {icon}
                      </div>
                      <div>
                        <p className={`text-sm font-bold ${i === arr.length - 1 ? 'text-white' : 'text-zinc-100'}`}>
                          {label}
                        </p>
                        <p className={`text-xs ${i === arr.length - 1 ? 'text-white/75' : 'text-zinc-400'}`}>
                          {sub}
                        </p>
                      </div>
                      {active && (
                        <div className="ml-auto shrink-0">
                          <svg className={`w-4 h-4 ${i === arr.length - 1 ? 'text-white' : 'text-[#0AC18E]'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </div>
                    {i < arr.length - 1 && (
                      <div className="flex justify-center py-1">
                        <div className="w-0.5 h-4 bg-zinc-700 rounded-full" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CTA — two actions: browse first, then enable */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://bchlocal.com"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-[#0AC18E] text-white text-base font-bold shadow-lg shadow-[#0AC18E]/30 hover:bg-[#09AF81] active:scale-95 transition-all"
            >
              <span>📍</span> Browse BCH Local
            </a>
            <Link
              to="/app#settings"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-zinc-800 border border-zinc-700 text-white text-base font-semibold hover:border-[#0AC18E]/50 active:scale-95 transition-all"
            >
              <span>⚡</span> Enable in the App
            </Link>
          </div>
          <p className="text-zinc-600 text-xs text-center mt-3">
            Opens Settings directly — just toggle on Merchant Discovery
          </p>
        </div>
      </section>

      {/* ── BCH Nostr community ── */}
      <section className="bg-zinc-950 border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-5 py-12">
          <div className="flex flex-col sm:flex-row items-center gap-6 bg-zinc-900 border border-zinc-800 rounded-3xl p-6 sm:p-8">
            {/* Nostr logo / icon */}
            <div className="shrink-0 w-16 h-16 rounded-2xl bg-purple-600/20 border border-purple-500/30 flex items-center justify-center text-3xl">
              🟣
            </div>
            <div className="flex-1 text-center sm:text-left">
              <div className="inline-flex items-center gap-1.5 bg-purple-500/15 border border-purple-500/25 rounded-full px-3 py-1 mb-3">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-pulse" />
                <span className="text-xs font-bold text-purple-400 uppercase tracking-wider">Active Community</span>
              </div>
              <h3 className="text-white font-black text-xl mb-2">
                Join BCH Nostr
              </h3>
              <p className="text-zinc-400 text-sm leading-relaxed max-w-lg">
                There's a growing community of BCH users, merchants, and developers on Nostr —
                a decentralised social network that runs on the same values as Bitcoin Cash.
                Ask questions, share your experience, and connect with people spending and accepting BCH every day.
              </p>
            </div>
            <div className="shrink-0">
              <a
                href="https://bchnostr.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-purple-600 text-white text-sm font-bold hover:bg-purple-700 active:scale-95 transition-all shadow-lg shadow-purple-900/40 whitespace-nowrap"
              >
                Visit BCH Nostr →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section id="how-it-works" className="bg-white dark:bg-zinc-900 border-y border-zinc-100 dark:border-zinc-800">
        <div className="max-w-4xl mx-auto px-5 py-16">
          <h2 className="text-2xl font-black text-center mb-2">How it works</h2>
          <p className="text-sm text-zinc-400 text-center mb-10">Three steps from zero to paid</p>
          <div className="grid sm:grid-cols-3 gap-6">
            {HOW_IT_WORKS.map(({ step, title, desc }) => (
              <div key={step} className="flex flex-col items-start gap-3">
                <div className="w-10 h-10 rounded-2xl bg-[#0AC18E] flex items-center justify-center shrink-0 shadow-sm shadow-[#0AC18E]/30">
                  <span className="text-white font-black text-base">{step}</span>
                </div>
                <div>
                  <p className="font-bold text-zinc-900 dark:text-zinc-50 mb-1">{title}</p>
                  <p className="text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="max-w-4xl mx-auto px-5 py-16">
        <h2 className="text-2xl font-black text-center mb-2">Everything you need</h2>
        <p className="text-sm text-zinc-400 text-center mb-10">Built for real merchants, not tech people</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map(({ icon, title, desc }) => (
            <div key={title} className="bg-white dark:bg-zinc-900 rounded-2xl p-5 border border-zinc-100 dark:border-zinc-800 hover:border-[#0AC18E]/30 transition-colors">
              <span className="text-2xl mb-3 block">{icon}</span>
              <p className="font-bold text-zinc-900 dark:text-zinc-50 mb-1.5">{title}</p>
              <p className="text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Install section ── */}
      <div className="bg-white dark:bg-zinc-900 border-y border-zinc-100 dark:border-zinc-800">
        <InstallSection />
      </div>

      {/* ── CTA ── */}
      <section className="bg-[#0AC18E]">
        <div className="max-w-4xl mx-auto px-5 py-16 text-center">
          <h2 className="text-3xl font-black text-white mb-3">Ready to get started?</h2>
          <p className="text-white/75 mb-8 text-base max-w-md mx-auto">
            Open the app, enter your Bitcoin Cash address, and accept your first payment in under a minute.
          </p>
          <Link
            to="/app"
            className="inline-block px-10 py-4 rounded-2xl bg-white text-[#0AC18E] text-base font-black shadow-xl shadow-black/10 hover:bg-zinc-50 active:scale-95 transition-all"
          >
            Launch POS Terminal →
          </Link>
          <p className="text-white/50 text-xs mt-5">Free to use · No sign-up · Works on any device</p>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-white dark:bg-zinc-900 border-t border-zinc-100 dark:border-zinc-800">
        <div className="max-w-4xl mx-auto px-5 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-[#0AC18E] flex items-center justify-center">
              <span className="text-white font-black text-[9px]">BCH</span>
            </div>
            <span className="text-sm font-bold text-zinc-700 dark:text-zinc-200">BCHPAY.Cash</span>
          </div>
          <div className="flex items-center gap-5 text-xs text-zinc-400">
            <a href="https://bitcoincash.org" target="_blank" rel="noopener noreferrer" className="hover:text-[#0AC18E] transition-colors">What is BCH?</a>
            <a href="https://bchexplorer.cash" target="_blank" rel="noopener noreferrer" className="hover:text-[#0AC18E] transition-colors">Block Explorer</a>
            <Link to="/merchants" className="hover:text-[#0AC18E] transition-colors">BCH Local</Link>
            <a href="#install" className="hover:text-[#0AC18E] transition-colors">Install</a>
            <Link to="/app" className="hover:text-[#0AC18E] transition-colors">Open App</Link>
          </div>
          <p className="text-xs text-zinc-400">
            Vibed with{' '}
            <a href="https://shakespeare.diy" target="_blank" rel="noopener noreferrer" className="text-[#0AC18E] hover:underline">Shakespeare</a>
          </p>
        </div>
      </footer>
    </div>
  );
}
